// Cognidoor Landing Page – Responsive, Desktop Unchanged

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthUser from '../services/useAuthUser';
import { useSubscription } from '../hooks/useSubscription';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getPlanDisplayName, legacyPlanToPlanId } from '../config/entitlements';
import LandingCanonical from '../designs/LandingCanonical';
import LandingWalkthroughsSection from '../components/landing/LandingWalkthroughsSection';
import LandingSectionDivider from '../components/landing/LandingSectionDivider';
import WhyCognidoorV4 from '../components/landing/WhyCognidoorV4';
import { RedRockDrillsCtaSlide } from '../components/landing/RedRockDrillsCtaSlide';
import SEO from '../components/SEO';
import { buildFaqPageStructuredData } from '../content/faqCatalog';

// Responsive custom styles (desktop unchanged)
const styles = `
  @keyframes infiniteScroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .coming-soon-banner {
    position: relative;
    overflow: hidden;
  }

  .coming-soon-banner::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: shine 3s infinite;
  }

  .coming-soon-text {
    position: relative;
    z-index: 1;
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    20% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  .infinite-scroll {
    animation: infiniteScroll 15s linear infinite;
    display: flex;
    gap: 0;
  }
  .infinite-scroll:hover { animation-play-state: paused; }
  .card-wrapper { position: relative; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; transform-style: preserve-3d; }
  .card-wrapper:hover { transform: translateY(-8px) scale(1.02); z-index: 10; }
  .integrated-card {
    box-shadow: inset 0 4px 12px rgba(0,0,0,0.5), inset 0 2px 6px rgba(0,0,0,0.6), inset 0 -2px 6px rgba(0,0,0,0.4), 0 10px 30px -10px rgba(66,153,225,0.4);
    background: linear-gradient(165deg,rgba(0,0,0,0.1) 0%,rgba(255,255,255,0.05) 100%);
    backdrop-filter: blur(5px); transform: translateZ(0); position: relative; overflow: hidden; border: 1px solid rgba(0,0,0,0.1);
  }
  .integrated-card::before { content: ''; position: absolute; inset: 0; background: linear-gradient(165deg,rgba(255,255,255,0.1) 0%,transparent 50%); mix-blend-mode: overlay; pointer-events: none; }
  .integrated-card::after { content: ''; position: absolute; inset: 0; box-shadow: inset 0 1px 1px rgba(255,255,255,0.1); pointer-events: none; }
  .integrated-card img { transform: scale(1.02); transition: all 0.6s cubic-bezier(0.4,0,0.2,1); filter: contrast(1.1) brightness(1.05) saturate(1.05); mix-blend-mode: multiply; }
  .card-wrapper:hover .integrated-card img { transform: scale(1.08); filter: contrast(1.15) brightness(1.1) saturate(1.1); }
  .card-content {
    position: absolute; inset: 0; padding: 1.5rem;
    background: linear-gradient(to top, rgba(0,0,0,0.85) 0%,rgba(0,0,0,0.6) 30%,transparent 70%);
    opacity: 0; transition: all 0.4s ease-in-out;
    display: flex; flex-direction: column; justify-content: flex-end; backdrop-filter: blur(3px);
  }
  .card-wrapper:hover .card-content { opacity: 1; }
  .section-title {
    background: linear-gradient(135deg,#000 0%,#333 25%,#000 50%,#666 75%,#000 100%);
    -webkit-background-clip: text; color: transparent; position: relative; display: inline-block; text-shadow: 0 1px 1px rgba(255,255,255,0.1);
  }
  .section-title::after { content: ''; position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%); width: 80px; height: 3px; background: linear-gradient(to right,transparent 0%,rgba(0,0,0,0.3) 25%,rgba(0,0,0,0.5) 50%,rgba(0,0,0,0.3) 75%,transparent 100%); border-radius: 2px; }
  .section-title::before { content: ''; position: absolute; top: -2px; left: 0; right: 0; height: 1px; background: linear-gradient(to right,transparent 0%,rgba(255,255,255,0.1) 50%,transparent 100%); }
  .feature-card { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); cursor: pointer; transform-style: preserve-3d; background: linear-gradient(165deg,rgba(255,255,255,0.95) 0%,rgba(255,255,255,0.98) 100%); opacity: 0; transform: translateY(20px); animation: fadeInUp 0.5s forwards; border: 1px solid rgba(59,130,246,0.1);}
  .feature-card:hover { transform: translateY(-4px) scale(1.02); box-shadow: 0 12px 24px -10px rgba(66,153,225,0.3); border-color: rgba(59,130,246,0.2); background: linear-gradient(165deg,rgba(255,255,255,0.98) 0%,rgba(239,246,255,0.9) 100%);}
  .feature-icon { transition: all 0.3s ease; display: inline-flex; align-items: center; justify-content: center; width: 50px; height: 50px; background: linear-gradient(135deg,#EFF6FF 0%,#DBEAFE 100%); border-radius: 12px; margin-bottom: 1rem;}
  .feature-card:hover .feature-icon { transform: scale(1.1) rotate(-5deg); background: linear-gradient(135deg,#DBEAFE 0%,#BFDBFE 100%);}
  .resource-card { transition: all 0.4s cubic-bezier(0.4,0,0.2,1); background: linear-gradient(165deg,rgba(255,255,255,0.98) 0%,rgba(239,246,255,0.95) 100%); opacity: 0; transform: translateY(20px); animation: fadeInUp 0.5s forwards; border: 1px solid rgba(59,130,246,0.1); position: relative; overflow: hidden;}
  .resource-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(to right,#60A5FA,#93C5FD); opacity: 0; transition: opacity 0.3s ease;}
  .resource-card:hover { transform: translateY(-4px) scale(1.02); box-shadow: 0 20px 30px -15px rgba(66,153,225,0.25); border-color: rgba(59,130,246,0.2);}
  .resource-card:hover::before { opacity: 1;}
  @keyframes fadeInUp { to { opacity: 1; transform: translateY(0); } }
  .pricing-card {
    transition: all 0.3s ease;
    position: relative;
  }
  .pricing-card.selected {
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(59,130,246,0.3);
    border-color: rgba(59,130,246,0.5);
    background: linear-gradient(to bottom right,rgba(59,130,246,0.05),rgba(59,130,246,0.02));
  }
  .pricing-card.faded {
    opacity: 0.7;
    filter: grayscale(0.2);
  }
  .pricing-card.faded > *:not(button) {
    opacity: 0.7;
    filter: grayscale(0.2);
  }
  .pricing-card button {
    cursor: pointer !important;
    pointer-events: auto !important;
    opacity: 1 !important;
    filter: none !important;
  }
  .pricing-card.selected::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    padding: 2px;
    background: linear-gradient(45deg, #60A5FA, #93C5FD);
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    z-index: 1;
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  /* MOBILE/TABLET SPECIFIC */
  @media (max-width: 640px) {
    .infinite-scroll {
      animation: infiniteScroll 8s linear infinite;
      width: 100%;
      justify-content: flex-start;
      gap: 0.5rem;
    }
    .infinite-scroll > div {
      flex: 0 0 calc(50% - 0.25rem);
      min-width: calc(50% - 0.25rem);
      max-width: calc(50% - 0.25rem);
    }
    .card-wrapper { 
      margin-bottom: 0.75rem;
      transform: scale(0.95);
    }
    .integrated-card { 
      min-height: 180px;
      aspect-ratio: 1/1.2;
    }
    .card-content { 
      padding: 0.75rem;
    }
    .card-content h3 {
      font-size: 0.9rem;
      margin-bottom: 0.25rem;
    }
    .card-content p {
      font-size: 0.8rem;
    }
    .card-content button {
      padding: 0.4rem 0.75rem;
      font-size: 0.8rem;
      margin-top: 0.5rem;
    }
    .feature-card, .resource-card { padding: 1rem; font-size: 0.96rem; }
    .section-title { font-size: 1.5rem !important; }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function LandingPage() {
  const [, setHighlightPlan] = useState(null);
  const location = useLocation();
  const { user } = useAuthUser();
  const { planId, isSubscriptionActive, loading: subscriptionLoading } = useSubscription();
  const hideFreeTrialBlocks =
    Boolean(user) && !subscriptionLoading && isSubscriptionActive && planId != null;
  const [, setUserPlan] = useState(null);
  const [hasReachedLiveDemos, setHasReachedLiveDemos] = useState(false);

  // Add effect to get user's subscription plan
  useEffect(() => {
    if (user) {
      const subRef = doc(db, 'users', user.uid, 'subscriptions', 'current');
      const unsubscribe = onSnapshot(subRef, 
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserPlan(getPlanDisplayName(data.plan_id || legacyPlanToPlanId(data.plan)) || 'Free');
          } else {
            setUserPlan('Free');
          }
        },
        (error) => {
          // console.error('Subscription listener error:', error);
          setUserPlan('Free');
        }
      );
      return () => unsubscribe();
    } else {
      setUserPlan(null);
    }
  }, [user]);

  // Scroll and highlight logic (pricing section is inside LandingCanonical with id="pricing")
  // Scroll runs with a short delay so DOM is ready; a second scroll after 600ms ensures we stay on pricing
  // if WhyCognidoorV4's sidebar logic would otherwise adjust scroll (we also fixed that component to not enter sidebar when pricing is in view).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlight = params.get('highlight');
    const stateHighlight = location.state?.highlight;
    const scrollToPricing = location.state?.scrollToPricing || location.hash === '#pricing';
    const scrollToLiveDemos = location.hash === '#live-demos';
    
    // Handle both URL params and location state
    const finalHighlight = highlight || stateHighlight;
    
    if (finalHighlight) {
      setHighlightPlan(finalHighlight);
    }
    if ((finalHighlight || scrollToPricing)) {
      const scrollPricingIntoView = () => {
        const pricingEl = document.getElementById('pricing');
        if (pricingEl) {
          pricingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      const t1 = setTimeout(scrollPricingIntoView, 150);
      const t2 = setTimeout(scrollPricingIntoView, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // Scroll to live demos when hash is #live-demos (e.g. "Preview a demo" link); content is track-aware (all/mck/bcg).
    if (scrollToLiveDemos) {
      const scrollLiveDemosIntoView = () => {
        const demosEl = document.getElementById('live-demos');
        if (demosEl) {
          demosEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      const t1 = setTimeout(scrollLiveDemosIntoView, 150);
      const t2 = setTimeout(scrollLiveDemosIntoView, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [location.search, location.state, location.hash]);

  // Start Red Rock drills CTAs only when user scrolls to the live demos section
  useEffect(() => {
    let observer = null;
    const attachObserver = (el) => {
      if (!el || observer) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) setHasReachedLiveDemos(true);
        },
        { rootMargin: '0px', threshold: 0.1 }
      );
      observer.observe(el);
    };
    const el = document.getElementById('live-demos');
    if (el) {
      attachObserver(el);
    } else {
      const id = setInterval(() => {
        const found = document.getElementById('live-demos');
        if (found) {
          clearInterval(id);
          attachObserver(found);
        }
      }, 150);
      return () => {
        clearInterval(id);
        if (observer) observer.disconnect();
      };
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const featureCards = [
    {
      eyebrow: "End-to-end coaching",
      icon: "🧭",
      accent: "from-sky-500 via-blue-500 to-cyan-400",
      title: "Coaching that carries you from assessments into interviews",
      text: "Get guided support across McKinsey and BCG prep, with coaching for digital assessments, AI interview practice, clear feedback, and the next step to fix after every session."
    },
    {
      eyebrow: "Realistic practice",
      icon: "🎯",
      accent: "from-emerald-500 via-teal-500 to-cyan-400",
      title: "Real simulators and AI interview practice that feel true to the firms",
      text: "Train with Sea Wolf, Red Rock, Casey, and realistic case practice for both firms, including McKinsey interviewer-led coaching, BCG candidate-led coaching, and preparation for the McKinsey Lilli experience."
    },
    {
      eyebrow: "Faster answers",
      icon: "⚡",
      accent: "from-indigo-500 via-sky-500 to-blue-400",
      title: "Faster answers, less Excel frustration, and better guided quant work",
      text: "Use Cognidoor's solvers and guided tools to work through the math cleanly, get to the answer faster, and spend more time thinking like a strong candidate under pressure."
    }
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <SEO
        title="McKinsey & BCG Interview and Assessment Prep"
        description="Prepare for McKinsey and BCG with AI interview coaches, deterministic simulators for Sea Wolf, Red Rock, and Casey, structured feedback, and progress tracking. One platform for assessments and interviews."
        url="/"
        structuredData={buildFaqPageStructuredData()}
      />
      {/* Canonical: Hero → Live demos → Why Cognidoor → Pricing */}
      <LandingCanonical>
        <LandingWalkthroughsSection />

        {/* Why Cognidoor */}
        <section id="why-cognidoor-section" className="py-8 sm:py-12 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-4 sm:px-6 md:px-10 xl:px-20 relative">
          <LandingSectionDivider />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 section-title tracking-tight">Why Cognidoor?</h2>
          <p className="max-w-4xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
            Everything you need to prepare for McKinsey and BCG assessments (Sea Wolf, Red Rock, Casey) and AI-led case interviews, with coaching, realistic practice, and tools that help you improve faster.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto items-stretch">
            {featureCards.map((card, index) => (
              <div
                key={index}
                className="feature-card group rounded-[28px] shadow-md p-5 sm:p-8 md:p-9 backdrop-blur-sm text-left min-h-[260px] sm:min-h-[320px] flex flex-col relative overflow-hidden"
                style={{ animationDelay: `${index * 0.12}s` }}
              >
                <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <div className={`absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br ${card.accent} blur-3xl opacity-20`} />
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/80 to-transparent" />
                </div>
                <div className={`h-1.5 w-24 rounded-full bg-gradient-to-r ${card.accent} transition-all duration-500 group-hover:w-32 group-hover:translate-x-1`} />
                <div className="mt-6 flex items-center gap-4">
                  <div className="feature-icon w-14 h-14 text-2xl mb-0 transition-all duration-500 group-hover:scale-110 group-hover:-rotate-6 group-hover:shadow-[0_14px_30px_-14px_rgba(59,130,246,0.45)]">
                    {card.icon}
                  </div>
                  <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-sky-700 transition-transform duration-500 group-hover:translate-x-1">
                    {card.eyebrow}
                  </div>
                </div>
                <h3 className="mt-6 text-xl sm:text-2xl font-semibold leading-tight text-gray-800 transition-colors duration-300 group-hover:text-slate-900">
                  {card.title}
                </h3>
                <p className="mt-4 text-[15px] sm:text-base text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-slate-700">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Cognidoor V4 · Signature Showcase — full page width */}
        <div className="w-full pt-2 sm:pt-4 pb-8 sm:pb-10">
          <WhyCognidoorV4 />
        </div>

        {/* Red Rock free practice CTA — mobile only: temporarily hidden, keep for future reuse.
        {!hideFreeTrialBlocks ? (
        <div className="block sm:hidden">
          <section className="py-8 sm:py-10 px-4 sm:px-6 bg-gradient-to-b from-blue-50/50 to-white border-y border-blue-100/60">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Start free before you simulate</h3>
              <p className="text-gray-600 text-sm sm:text-base mb-5 sm:mb-6 max-w-2xl mx-auto">
                Try Red Rock-style practice cases for free. Train chart reading, math, logic, and recommendations under time pressure before moving into the real simulator.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link
                  to="/redrock-guide"
                  className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-sm sm:text-base font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Start Free Practice
                </Link>
                <Link
                  to="/redrock"
                  className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-blue-600 text-blue-600 text-sm sm:text-base font-semibold rounded-xl hover:bg-blue-50 transition-all duration-200"
                >
                  See Red Rock Simulator
                </Link>
              </div>
            </div>
          </section>
        </div>
        ) : null}
        */}
      </LandingCanonical>
      {/* Sliding Red Rock drills CTA: one pop-up, 10s visible / 25s hidden, first when user reaches live demos */}
      {!hideFreeTrialBlocks && hasReachedLiveDemos && <RedRockDrillsCtaSlide top="50%" initialDelayMs={0} />}
    </div>
  );
}
